import { useCallback } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useAddInventoryToServiceMutation,
  useAddServiceItemMutation,
  useCopyPartsTechPartsToServiceMutation,
  useCreateLaborOverrideMutation,
  useCreatePartOverrideMutation,
  useUpdateServiceMutation
} from '../../../redux/api'
import { ServiceExpanded, ServiceInfoStatusEnum } from '../../../sdk'
import {
  convertOldLaborOverride,
  convertOldPartOverride,
  getLaborAndPartsFromOldService,
  getNewChildrenFromService
} from './transformSelectedJob'

export const useCopyService = () => {
  const { accessToken } = useAccessTokenAndShopId()

  const [createLaborOverride] = useCreateLaborOverrideMutation()
  const [createPartOverride] = useCreatePartOverrideMutation()
  const [addServiceItem] = useAddServiceItemMutation()
  const [updateService] = useUpdateServiceMutation()
  const [addInventoryToService] = useAddInventoryToServiceMutation()
  const [copyPartsTechParts] = useCopyPartsTechPartsToServiceMutation()

  const handleSubmitCopyService = useCallback(
    async (
      repairOrderId: string,
      service: ServiceExpanded,
      selectedLaborMatrixId: string,
      setIsSubmitting: (s: boolean) => void,
      submitCallback: (serviceIds: string[]) => void
    ) => {
      try {
        setIsSubmitting(true)
        const serviceId = await addServiceItem({
          authorization: accessToken,
          serviceInfo: {
            ...service.entry!.info,
            repairOrder: repairOrderId,
            status: ServiceInfoStatusEnum.Recommended,
            recommendInFutureRepairOrders: true
          }
        }).unwrap()
        const promises = []
        // set old service to not show anymore
        promises.push(
          updateService({
            authorization: accessToken,
            serviceEntry: {
              ...service.entry,
              info: {
                ...service.entry!.info!,
                recommendInFutureRepairOrders: false
              }
            }
          })
        )
        const {
          oldChildren,
          oldParents,
          oldCustomOrSingleMotor,
          oldStandsAlone,
          oldClaimed,
          oldInCart
        } = getLaborAndPartsFromOldService(service)
        // Add custom labors or single motor labors
        if (oldCustomOrSingleMotor.length > 0) {
          promises.push(
            createLaborOverride({
              authorization: accessToken,
              createLaborOverridesRequest: {
                service: serviceId,
                laborOverrides: convertOldLaborOverride(
                  oldCustomOrSingleMotor,
                  serviceId,
                  selectedLaborMatrixId
                )
              }
            })
          )
        }
        // Add custom parts
        for (const oldPart of oldStandsAlone) {
          promises.push(
            createPartOverride({
              authorization: accessToken,
              partOverride: convertOldPartOverride(oldPart, serviceId)
            })
          )
        }
        // Add parent and child motor labors
        if (oldParents.length > 0) {
          const uuids = await createLaborOverride({
            authorization: accessToken,
            createLaborOverridesRequest: {
              service: serviceId,
              laborOverrides: convertOldLaborOverride(
                oldParents,
                serviceId,
                selectedLaborMatrixId
              )
            }
          }).unwrap()
          const newChildren = getNewChildrenFromService(
            uuids,
            oldParents,
            oldChildren,
            serviceId,
            selectedLaborMatrixId
          )
          promises.push(
            createLaborOverride({
              authorization: accessToken,
              createLaborOverridesRequest: {
                service: serviceId,
                laborOverrides: newChildren
              }
            })
          )
        }
        // link linked parts
        oldClaimed.forEach((override) => {
          const partsToLink = Object.values(
            service.partOverrideToLinkIdInventory![override.uuid!]!
          )
          promises.push(
            addInventoryToService({
              authorization: accessToken,
              addInventoryToServiceRequest: {
                service: serviceId,
                inventoryInstanceIds: partsToLink.map((p) => p.id!),
                unitOfMeasure: override.unitOfMeasure
                // markup is left undefined in order to get shop default markup vs markups from old service
              }
            })
          )
        })
        // add parts in cart
        if (oldInCart.length > 0) {
          promises.push(
            copyPartsTechParts({
              authorization: accessToken,
              copyPartsTechPartsToServiceRequest: {
                service: serviceId,
                partEntries: oldInCart
              }
            })
          )
        }
        await Promise.all(promises)
        submitCallback([serviceId])
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      accessToken,
      addInventoryToService,
      addServiceItem,
      copyPartsTechParts,
      createLaborOverride,
      createPartOverride,
      updateService
    ]
  )

  return {
    handleSubmitCopyService
  }
}
